import type { AdaptiveProfile } from './adaptive-experience';

export type RelationshipWeatherBand = 'calm' | 'variable' | 'storm';
export type RelationshipWeatherDirection = 'improving' | 'steady' | 'worsening';

export type RelationshipDriftSignal = {
  key: 'initiation' | 'latency' | 'emotionalDensity' | 'availability' | 'withdrawal';
  label: string;
  driftDelta: number;
  severity: 'low' | 'medium' | 'high';
};

export type RelationshipInterventionWindow = {
  label: string;
  startsInHours: number;
  confidence: number;
  reason: string;
};

export type RelationshipIntelligenceModel = {
  generatedAt: number;
  sampleCount: number;
  scoreNow: number | null;
  weatherBand: RelationshipWeatherBand;
  direction: RelationshipWeatherDirection;
  volatility: number;
  dataQuality: number;
  topSignals: RelationshipDriftSignal[];
  interventionWindows: RelationshipInterventionWindow[];
  summary: string;
};

export type CounterfactualStyle = 'soft_startup' | 'direct_repair' | 'space_then_reconnect';

export type CounterfactualProjection = {
  style: CounterfactualStyle;
  delta24h: number;
  projectedScore24h: number | null;
  confidence: number;
  rationale: string;
};

export type CounterfactualScript = {
  opener: string;
  followUp: string;
  avoidLine: string;
};

export type CounterfactualOutcome = {
  at: number;
  style: CounterfactualStyle;
  predictedDelta: number;
  actualDelta: number;
  scoreBefore: number;
  scoreAfter: number;
  sampleCountBefore: number;
  sampleCountAfter: number;
};

export type CoupleOperatingSystemScore = {
  overall: number;
  stability: number;
  repairVelocity: number;
  relapseRisk: number;
  weekDelta: number;
  label: 'fragile' | 'recovering' | 'resilient';
};

export type RelationshipTwinSnapshot = {
  partnerReadiness: number;
  overwhelmRisk: number;
  trustMomentum: number;
  bestWindowHours: number;
  nextBestMove: string;
};

export type RepairCompilerStep = {
  id: string;
  title: string;
  why: string;
  timing: string;
  expectedDelta: number;
};

export type RepairCompilerPlan = {
  dayTheme: string;
  expectedTotalDelta: number;
  confidence: number;
  steps: RepairCompilerStep[];
};

export type RelationshipConfidenceBand = {
  score: number;
  level: 'low' | 'medium' | 'high';
  label: string;
  rationale: string;
};

export type RelationshipQuickWin = {
  id: string;
  title: string;
  instruction: string;
  expectedDelta: number;
  timing: string;
};

export type RelationshipAdaptiveChallenge = {
  id: string;
  title: string;
  description: string;
  targetMetric: 'stability' | 'repair_velocity' | 'trust' | 'overwhelm';
  difficulty: 'light' | 'moderate' | 'intense';
  estimatedMinutes: number;
  expectedDelta: number;
  confidence: number;
};

type CheckinLike = {
  rtiScore?: number | string | null;
  timestamp?: string | number | null;
  date?: string | number | null;
  components?: Record<string, unknown> | null;
};

const COUNTERFACTUAL_OUTCOMES_STORAGE_KEY = 'tension_counterfactual_outcomes_v1';

function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function avg(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function stddev(values: number[]): number {
  if (values.length <= 1) return 0;
  const m = avg(values);
  const variance = avg(values.map((x) => (x - m) ** 2));
  return Math.sqrt(variance);
}

function parseTime(c: CheckinLike): number {
  const raw = c.timestamp ?? c.date ?? null;
  if (raw == null) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function severityFromDelta(delta: number): 'low' | 'medium' | 'high' {
  const d = Math.abs(delta);
  if (d >= 10) return 'high';
  if (d >= 5) return 'medium';
  return 'low';
}

function signalLabel(key: RelationshipDriftSignal['key']): string {
  if (key === 'emotionalDensity') return 'Emotional intensity';
  if (key === 'availability') return 'Emotional availability';
  return key[0].toUpperCase() + key.slice(1);
}

function confidenceLabel(level: RelationshipConfidenceBand['level']): string {
  if (level === 'high') return 'High confidence';
  if (level === 'medium') return 'Medium confidence';
  return 'Low confidence';
}

function deriveInterventionWindows(
  scores: number[],
  latestTimestamp: number,
  adaptive: AdaptiveProfile | null,
): RelationshipInterventionWindow[] {
  if (scores.length < 4 || latestTimestamp <= 0) {
    return [
      {
        label: 'Stability check-in',
        startsInHours: 18,
        confidence: 0.48,
        reason: 'More samples needed to personalize timing.',
      },
    ];
  }

  const tail = scores.slice(-8);
  const volatility = stddev(tail);
  const delta = tail[tail.length - 1] - tail[0];
  const baselineWindow = volatility > 11 ? 4 : volatility > 7 ? 8 : 14;
  const trendWindow = delta > 5 ? 3 : delta < -5 ? 18 : 10;
  const adaptiveBoost = adaptive?.focus === 'repair' ? -2 : adaptive?.focus === 'connection' ? 3 : 0;
  const firstHours = Math.max(2, baselineWindow + adaptiveBoost);
  const secondHours = Math.max(6, trendWindow + Math.round(volatility / 4));

  const firstConfidence = Math.min(0.92, 0.54 + Math.min(0.28, scores.length / 80 + volatility / 100));
  const secondConfidence = Math.min(0.9, 0.5 + Math.min(0.26, scores.length / 90 + Math.abs(delta) / 80));

  return [
    {
      label: 'Primary intervention window',
      startsInHours: firstHours,
      confidence: Number(firstConfidence.toFixed(2)),
      reason:
        delta > 4
          ? 'Rising tension suggests earlier preventive intervention.'
          : delta < -4
            ? 'Current recovery trend allows a later reinforcement window.'
            : 'Pattern cadence suggests a medium-horizon check-in window.',
    },
    {
      label: 'Reinforcement window',
      startsInHours: secondHours,
      confidence: Number(secondConfidence.toFixed(2)),
      reason: 'Follow-up window to lock in de-escalation before the next likely spike.',
    },
  ];
}

export function buildRelationshipIntelligenceModel(
  checkins: CheckinLike[],
  adaptive: AdaptiveProfile | null = null,
): RelationshipIntelligenceModel {
  const sorted = [...checkins].sort((a, b) => parseTime(a) - parseTime(b));
  const recent = sorted.slice(-42);
  const scores = recent.map((c) => num(c.rtiScore)).filter((v): v is number => v != null);
  const scoreNow = scores.length > 0 ? scores[scores.length - 1] : null;
  const firstHalf = scores.slice(0, Math.floor(scores.length / 2));
  const secondHalf = scores.slice(Math.floor(scores.length / 2));
  const trendDelta = secondHalf.length > 0 ? avg(secondHalf) - avg(firstHalf.length > 0 ? firstHalf : secondHalf) : 0;
  const volatility = Number(stddev(scores).toFixed(2));

  const weatherBand: RelationshipWeatherBand =
    scoreNow == null ? 'variable' : scoreNow <= 35 ? 'calm' : scoreNow <= 60 ? 'variable' : 'storm';
  const direction: RelationshipWeatherDirection =
    trendDelta > 3.5 ? 'worsening' : trendDelta < -3.5 ? 'improving' : 'steady';

  const componentKeys: Array<RelationshipDriftSignal['key']> = [
    'initiation',
    'latency',
    'emotionalDensity',
    'availability',
    'withdrawal',
  ];
  const topSignals = componentKeys
    .map((key) => {
      const vals = recent
        .map((c) => num((c.components ?? {})[key]))
        .filter((v): v is number => v != null);
      const half = Math.floor(vals.length / 2);
      if (vals.length < 4 || half <= 0) return null;
      const d = avg(vals.slice(half)) - avg(vals.slice(0, half));
      return {
        key,
        label: signalLabel(key),
        driftDelta: Number(d.toFixed(2)),
        severity: severityFromDelta(d),
      } as RelationshipDriftSignal;
    })
    .filter((v): v is RelationshipDriftSignal => v != null)
    .sort((a, b) => Math.abs(b.driftDelta) - Math.abs(a.driftDelta))
    .slice(0, 3);

  const dataQuality = Math.min(100, Math.round(scores.length * 1.6 + (adaptive ? adaptive.dataQuality.score * 0.25 : 0)));
  const latestTs = recent.length > 0 ? parseTime(recent[recent.length - 1]) : 0;
  const interventionWindows = deriveInterventionWindows(scores, latestTs, adaptive);

  const summary =
    scoreNow == null
      ? 'Not enough check-ins yet to generate a full relationship forecast.'
      : `Weather is ${weatherBand} and trend is ${direction}. Volatility ${volatility.toFixed(1)} with ${topSignals.length} active drift signals.`;

  return {
    generatedAt: Date.now(),
    sampleCount: scores.length,
    scoreNow,
    weatherBand,
    direction,
    volatility,
    dataQuality,
    topSignals,
    interventionWindows,
    summary,
  };
}

export function simulateCounterfactual(
  model: RelationshipIntelligenceModel,
  style: CounterfactualStyle,
  adaptive: AdaptiveProfile | null = null,
): CounterfactualProjection {
  const score = model.scoreNow;
  const base = score == null ? 50 : score;
  const weatherPenalty = model.weatherBand === 'storm' ? -1.8 : model.weatherBand === 'variable' ? -0.6 : 0.8;
  const directionPenalty = model.direction === 'worsening' ? -1.6 : model.direction === 'improving' ? 1.2 : 0.2;
  const volatilityPenalty = -(model.volatility / 8);

  const calibration = getCounterfactualCalibration()[style];
  let styleEffect = 0;
  let rationale = '';
  if (style === 'soft_startup') {
    styleEffect = model.weatherBand === 'storm' ? 5.2 : 3.4;
    rationale = 'Soft startup usually lowers defensiveness and improves near-term recovery odds.';
  } else if (style === 'direct_repair') {
    styleEffect = model.direction === 'worsening' ? 3.8 : 2.2;
    rationale = 'Direct repair works best when both people still have emotional bandwidth for explicit repair.';
  } else {
    styleEffect = model.volatility > 9 ? 4.6 : 2.6;
    rationale = 'Short space then reconnect helps when volatility suggests escalation risk from immediate engagement.';
  }

  const adaptiveBias =
    adaptive?.focus === 'repair' ? 1.3 : adaptive?.focus === 'communication' ? 0.7 : adaptive?.focus === 'connection' ? 0.4 : 0;
  const calibrationBias = calibration.samples > 0 ? calibration.avgResidual * 0.45 : 0;
  const delta24h = Number(
    (styleEffect + weatherPenalty + directionPenalty + volatilityPenalty + adaptiveBias + calibrationBias).toFixed(1),
  );
  const projectedScore24h = Number(Math.max(0, Math.min(100, base - delta24h)).toFixed(1));
  const confidence = Number(
    Math.min(
      0.93,
      0.5 +
        Math.min(0.3, model.dataQuality / 320 + model.sampleCount / 140 + Math.abs(delta24h) / 22) +
        Math.min(0.08, calibration.samples / 120),
    ).toFixed(2),
  );

  return {
    style,
    delta24h,
    projectedScore24h,
    confidence,
    rationale,
  };
}

export function buildCounterfactualScript(
  style: CounterfactualStyle,
  model: RelationshipIntelligenceModel,
  twin: RelationshipTwinSnapshot,
): CounterfactualScript {
  if (style === 'soft_startup') {
    return {
      opener:
        twin.overwhelmRisk >= 70
          ? "I care about us and I don't want this to become another painful loop. Can we take one small step to reset?"
          : "I care about us and want to understand your side better. Can we try a calm reset together?",
      followUp:
        model.weatherBand === 'storm'
          ? 'Could we each share one thing we need tonight to feel safer and heard?'
          : 'What is one small thing we can each do in the next 24 hours to feel closer?',
      avoidLine: "You're always impossible when this happens.",
    };
  }
  if (style === 'direct_repair') {
    return {
      opener:
        "I want to repair this with you directly: I own my part, and I want us to leave this conversation better than we entered it.",
      followUp:
        twin.partnerReadiness >= 60
          ? 'Can we agree on one concrete repair action each before tonight ends?'
          : 'Can we pick one specific misunderstanding to clear first, then pause and reassess?',
      avoidLine: 'Can we just stop pretending everything is fine?',
    };
  }
  return {
    opener: "I'm going to take a short pause so I can come back calmer and more respectful—this matters to me.",
    followUp:
      'I will message you again in a few hours with one clear ask and one appreciation so we can reconnect without more escalation.',
    avoidLine: "Forget it. I'm done talking.",
  };
}

export function buildRelationshipTwin(
  model: RelationshipIntelligenceModel,
  adaptive: AdaptiveProfile | null = null,
): RelationshipTwinSnapshot {
  const score = model.scoreNow ?? 52;
  const readinessBase = 100 - score;
  const readiness = Math.max(5, Math.min(95, readinessBase - model.volatility * 1.2 + (adaptive?.focus === 'connection' ? 6 : 0)));
  const overwhelmRisk = Math.max(
    5,
    Math.min(95, score * 0.9 + model.volatility * 2 + (model.direction === 'worsening' ? 8 : model.direction === 'improving' ? -6 : 0)),
  );
  const trustMomentum = Math.max(
    0,
    Math.min(
      100,
      58 +
        (model.direction === 'improving' ? 16 : model.direction === 'worsening' ? -14 : 0) +
        (adaptive?.focus === 'repair' ? -4 : adaptive?.focus === 'connection' ? 7 : 0),
    ),
  );
  const bestWindowHours = model.interventionWindows[0]?.startsInHours ?? 12;
  const nextBestMove =
    overwhelmRisk >= 70
      ? 'Use a calming soft-start message, then wait before deeper conversation.'
      : readiness >= 60
        ? 'Open with appreciation + one concrete repair ask in the primary window.'
        : 'Keep the next touchpoint short and safety-focused before any heavy topic.';

  return {
    partnerReadiness: Number(readiness.toFixed(1)),
    overwhelmRisk: Number(overwhelmRisk.toFixed(1)),
    trustMomentum: Number(trustMomentum.toFixed(1)),
    bestWindowHours,
    nextBestMove,
  };
}

export function compileRepairPlan(
  model: RelationshipIntelligenceModel,
  twin: RelationshipTwinSnapshot,
  adaptive: AdaptiveProfile | null = null,
): RepairCompilerPlan {
  const weather = model.weatherBand;
  const baseTheme =
    weather === 'storm' ? 'Stabilize first, repair second' : weather === 'variable' ? 'Targeted de-escalation + clarity' : 'Connection compounding day';
  const focusHint =
    adaptive?.focus === 'repair'
      ? 'repair-first'
      : adaptive?.focus === 'communication'
        ? 'communication-first'
        : adaptive?.focus === 'connection'
          ? 'connection-first'
          : 'balanced';

  const steps: RepairCompilerStep[] = [
    {
      id: 'step-1',
      title: 'Calm opener',
      why: twin.overwhelmRisk > 70 ? 'Overwhelm risk is high; reduce heat first.' : 'Opener quality strongly shifts next 24h outcomes.',
      timing: `T+${Math.max(1, twin.bestWindowHours - 2)}h`,
      expectedDelta: weather === 'storm' ? 2.4 : 1.5,
    },
    {
      id: 'step-2',
      title: 'Micro-repair ask',
      why: 'Concrete, low-load requests reduce ambiguity and escalation.',
      timing: `T+${twin.bestWindowHours}h`,
      expectedDelta: twin.partnerReadiness >= 55 ? 2.8 : 1.7,
    },
    {
      id: 'step-3',
      title: 'Reinforcement loop',
      why: 'Follow-through preserves momentum and prevents relapse.',
      timing: `T+${Math.max(twin.bestWindowHours + 8, 16)}h`,
      expectedDelta: focusHint === 'connection-first' ? 2.1 : 1.4,
    },
  ];

  const expectedTotalDelta = Number(steps.reduce((sum, s) => sum + s.expectedDelta, 0).toFixed(1));
  const confidence = Number(
    Math.min(0.91, 0.52 + model.dataQuality / 260 + model.sampleCount / 180 - model.volatility / 90).toFixed(2),
  );

  return {
    dayTheme: `${baseTheme} (${focusHint})`,
    expectedTotalDelta,
    confidence: Math.max(0.35, confidence),
    steps,
  };
}

export function buildRelationshipConfidenceBand(
  model: RelationshipIntelligenceModel,
): RelationshipConfidenceBand {
  const raw =
    model.dataQuality * 0.58 +
    Math.min(40, model.sampleCount) * 1.05 -
    Math.min(20, model.volatility) * 0.85 +
    (model.direction === 'steady' ? 4 : model.direction === 'improving' ? 6 : -2);
  const score = clamp(Math.round(raw), 8, 98);
  const level: RelationshipConfidenceBand['level'] = score >= 75 ? 'high' : score >= 45 ? 'medium' : 'low';
  const rationale =
    level === 'high'
      ? 'Model has enough stable data to support decisive next-step guidance.'
      : level === 'medium'
        ? 'Model is directionally reliable, but outcomes still benefit from short feedback loops.'
        : 'Model needs more check-ins before strong directional commitments.';
  return {
    score,
    level,
    label: confidenceLabel(level),
    rationale,
  };
}

export function buildRelationshipQuickWins(
  model: RelationshipIntelligenceModel,
  twin: RelationshipTwinSnapshot,
): RelationshipQuickWin[] {
  const base: RelationshipQuickWin[] = [
    {
      id: 'quick-win-opener',
      title: 'Lower temperature opener',
      instruction:
        twin.overwhelmRisk >= 70
          ? 'Start with one calming sentence and one appreciation before any ask.'
          : 'Start with appreciation + one specific ask with no blame language.',
      expectedDelta: twin.overwhelmRisk >= 70 ? 2.3 : 1.7,
      timing: `Within ${Math.max(1, twin.bestWindowHours - 2)}h`,
    },
    {
      id: 'quick-win-clarity',
      title: 'Single-issue focus',
      instruction: 'Address only one misunderstanding in the next conversation to avoid cognitive overload.',
      expectedDelta: model.weatherBand === 'storm' ? 2.1 : 1.4,
      timing: `At +${twin.bestWindowHours}h`,
    },
    {
      id: 'quick-win-close-loop',
      title: 'Close the loop',
      instruction: 'End with one concrete commitment each and a same-day check-back message.',
      expectedDelta: model.direction === 'improving' ? 1.6 : 2.2,
      timing: 'Same day reinforcement',
    },
  ];
  return base;
}

export function buildAdaptiveChallenges(
  model: RelationshipIntelligenceModel,
  twin: RelationshipTwinSnapshot,
  osScore: CoupleOperatingSystemScore,
): RelationshipAdaptiveChallenge[] {
  const confidence = Math.min(0.94, 0.48 + model.dataQuality / 220 + model.sampleCount / 200);
  const challenges: RelationshipAdaptiveChallenge[] = [
    {
      id: 'challenge-clarity-arc',
      title: 'Clarity Arc Sprint',
      description: 'Run a two-message repair arc: calm opener now, micro-repair ask in the primary window.',
      targetMetric: 'repair_velocity',
      difficulty: model.weatherBand === 'storm' ? 'intense' : 'moderate',
      estimatedMinutes: 7,
      expectedDelta: Number((2.4 + (model.direction === 'worsening' ? 0.7 : 0.2)).toFixed(1)),
      confidence: Number(confidence.toFixed(2)),
    },
    {
      id: 'challenge-overwhelm-buffer',
      title: 'Overwhelm Buffer',
      description: 'Insert a guided pause + reconnection checkpoint to cut overload before deeper topics.',
      targetMetric: 'overwhelm',
      difficulty: twin.overwhelmRisk >= 68 ? 'moderate' : 'light',
      estimatedMinutes: 5,
      expectedDelta: Number((1.8 + (twin.overwhelmRisk >= 68 ? 0.6 : 0.2)).toFixed(1)),
      confidence: Number((confidence - 0.03).toFixed(2)),
    },
    {
      id: 'challenge-trust-compound',
      title: 'Trust Compound Loop',
      description: 'Execute one appreciation loop and one follow-through action to compound trust momentum.',
      targetMetric: 'trust',
      difficulty: osScore.label === 'fragile' ? 'moderate' : 'light',
      estimatedMinutes: 6,
      expectedDelta: Number((2.1 + (osScore.label === 'fragile' ? 0.4 : 0.1)).toFixed(1)),
      confidence: Number((confidence - 0.05).toFixed(2)),
    },
  ];
  return challenges;
}

export function readCounterfactualOutcomes(): CounterfactualOutcome[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = JSON.parse(localStorage.getItem(COUNTERFACTUAL_OUTCOMES_STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (r) =>
        r &&
        typeof r.at === 'number' &&
        typeof r.style === 'string' &&
        typeof r.predictedDelta === 'number' &&
        typeof r.actualDelta === 'number',
    ) as CounterfactualOutcome[];
  } catch {
    return [];
  }
}

export function recordCounterfactualOutcome(outcome: CounterfactualOutcome): void {
  if (typeof localStorage === 'undefined') return;
  const existing = readCounterfactualOutcomes();
  const merged = [...existing, outcome].slice(-240);
  try {
    localStorage.setItem(COUNTERFACTUAL_OUTCOMES_STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // ignore quota limits
  }
}

export function getCounterfactualCalibration(): Record<CounterfactualStyle, { samples: number; avgResidual: number }> {
  const list = readCounterfactualOutcomes();
  const init = () => ({ samples: 0, avgResidual: 0 });
  const map: Record<CounterfactualStyle, { samples: number; avgResidual: number }> = {
    soft_startup: init(),
    direct_repair: init(),
    space_then_reconnect: init(),
  };
  const buckets: Record<CounterfactualStyle, number[]> = {
    soft_startup: [],
    direct_repair: [],
    space_then_reconnect: [],
  };
  for (const item of list) {
    const residual = item.actualDelta - item.predictedDelta;
    buckets[item.style].push(residual);
  }
  (Object.keys(buckets) as CounterfactualStyle[]).forEach((k) => {
    const arr = buckets[k];
    if (arr.length === 0) return;
    map[k] = {
      samples: arr.length,
      avgResidual: Number((arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2)),
    };
  });
  return map;
}

export function buildCoupleOperatingSystemScore(
  checkins: CheckinLike[],
  model: RelationshipIntelligenceModel,
): CoupleOperatingSystemScore {
  const sorted = [...checkins].sort((a, b) => parseTime(a) - parseTime(b));
  const scores = sorted.map((c) => num(c.rtiScore)).filter((v): v is number => v != null);
  if (scores.length < 3) {
    return {
      overall: 50,
      stability: 50,
      repairVelocity: 50,
      relapseRisk: 50,
      weekDelta: 0,
      label: 'recovering',
    };
  }

  const diffs: number[] = [];
  for (let i = 1; i < scores.length; i += 1) {
    diffs.push(Math.abs(scores[i] - scores[i - 1]));
  }
  const avgDiff = avg(diffs);
  const stability = clamp(Math.round(100 - avgDiff * 5.8 - model.volatility * 1.4), 0, 100);

  const recent = scores.slice(-8);
  const prior = scores.slice(-16, -8);
  const recentAvg = recent.length ? avg(recent) : avg(scores.slice(-6));
  const priorAvg = prior.length ? avg(prior) : avg(scores.slice(0, Math.min(8, scores.length)));
  const repairVelocityRaw = priorAvg - recentAvg;
  const repairVelocity = clamp(Math.round(50 + repairVelocityRaw * 4.6), 0, 100);

  const stormPenalty = model.weatherBand === 'storm' ? 20 : model.weatherBand === 'variable' ? 9 : 2;
  const relapseRisk = clamp(
    Math.round(30 + model.volatility * 3.2 + stormPenalty + (model.direction === 'worsening' ? 12 : model.direction === 'improving' ? -8 : 0)),
    0,
    100,
  );

  const overall = clamp(Math.round(stability * 0.42 + repairVelocity * 0.34 + (100 - relapseRisk) * 0.24), 0, 100);
  const weekDelta = Math.round((priorAvg - recentAvg) * 10) / 10;
  const label: CoupleOperatingSystemScore['label'] =
    overall >= 74 ? 'resilient' : overall >= 52 ? 'recovering' : 'fragile';

  return {
    overall,
    stability,
    repairVelocity,
    relapseRisk,
    weekDelta,
    label,
  };
}
